import axiosInstance from '@/config/http.config'
import { useAuthStore } from '@/features/auth/stores/AuthStore'

const getPlantId = () => {
  const authStore = useAuthStore()
  return authStore.plantId
}
export const getUserPreferences = async (viewKey = 'counter') => {
  const response = await axiosInstance.get(`/user-preferences/${viewKey}`)
  return response.data
}

export const saveUserPreferences = async (visibleColumns, allColumns, viewKey = 'counter') => {
  const columns = allColumns.map((columnId, index) => ({
    columnId,
    visible: visibleColumns.includes(columnId),
    order: visibleColumns.includes(columnId)
      ? visibleColumns.indexOf(columnId)
      : index,
  }))

  const response = await axiosInstance.post('/user-preferences', {
    viewKey,
    columns,
  })

  return response.data
}

export const getUserPrintPreferences = async () => {
  const response = await axiosInstance.get('/user-print-preferences', {
    params: {
      plantId: getPlantId(),
    },
  })

  return response.data
}

// data.firma va como { hoja1: {x,y,width,height,rotation}, hoja2: {...} } —
// el backend ya valida y persiste ese formato anidado (un recuadro de firma
// por hoja) vía FirmaPositionDto.
// Guardado COMPLETO: crea o reemplaza márgenes + firma + plantilla juntos.
// Usar solo cuando también se están guardando los márgenes (modal "Formato").
export const saveUserPrintPreferences = async (data) => {
  const body = {
    marginTop: data.marginTop,
    marginBottom: data.marginBottom,
    marginLeft: data.marginLeft,
    marginRight: data.marginRight,
    firmaUrl: data.firmaUrl ?? null,
    firma: data.firma ?? null,
  }
  const response = await axiosInstance.post('/user-print-preferences', body, {
    params: { plantId: getPlantId() },
  })
  return response.data
}

// Guardado PARCIAL: para "arrastrar la firma en el editor y guardar
// posición" — solo envía lo que cambió (típicamente firmaUrl + firma), sin
// tocar márgenes ni exigir reenviarlos. El backend conserva cualquier campo
// omitido (ver PatchUserPrintPreferenceDto).
export const patchUserPrintPreferences = async (data) => {
  const body = {}
  if (data.firmaUrl !== undefined) body.firmaUrl = data.firmaUrl
  if (data.firma !== undefined) body.firma = data.firma
  if (data.marginTop !== undefined) body.marginTop = data.marginTop
  if (data.marginBottom !== undefined) body.marginBottom = data.marginBottom
  if (data.marginLeft !== undefined) body.marginLeft = data.marginLeft
  if (data.marginRight !== undefined) body.marginRight = data.marginRight

  const response = await axiosInstance.patch('/user-print-preferences', body, {
    params: { plantId: getPlantId() },
  })
  return response.data
}



export const getUserPrintTemplate = async () => {
  const response = await axiosInstance.get('/user-print-templates', {
    params: {
      plantId: getPlantId(),
    },
  })

  return response.data
}


export const uploadUserPrintTemplate = async (templateFile) => {
  const formData = new FormData()
  formData.append('template', templateFile)

  const response = await axiosInstance.post(
    '/user-print-templates',
    formData,
    {
      params: {
        plantId: getPlantId(),
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )

  return response.data
}


export const deleteUserPrintTemplate = async (plantId) => {
  const response = await axiosInstance.delete('/user-print-templates', {
    params: { plantId }
  })

  return response.data
}


export const uploadUserFirma = async (firmaFile) => {
  const formData = new FormData()
  formData.append('image', firmaFile)
  const plantId = getPlantId()
  const response = await axiosInstance.post(
    '/user-print-templates/firma',
    formData,
    {
      params: {
        plantId,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return response.data
}

export const createPlantPreferences = async (data) => {
  const response = await axiosInstance.post('/plant-preferences', data)
  return response.data
}

export const getPlantPreferences = async (plantId) => {
  const response = await axiosInstance.get(
    `/plant-preferences/plant/${plantId}`
  )

  return response.data
}

export const updatePlantPreferences = async (plantId, data) => {
  const response = await axiosInstance.put(
    `/plant-preferences/${plantId}`,
    data
  )

  return response.data
}


export const getEmissionValues = async ({
  category,
  fuel,
  manufacturingYear,
  altitude
}) => {
  const response = await axiosInstance.get('/emissions/values', {
    params: {
      category,
      fuel,
      manufacturingYear,
      altitude
    }
  })

  return response.data
}

export const getRoleAccessConfig = async (plantId, role) => {
  const response = await axiosInstance.get(
    `/role-access-config/${plantId}?role=${role}`
  )

  return response.data
}

export const saveRoleAccessConfig = async (data) => {
  const response = await axiosInstance.post('/role-access-config', data)
  return response.data
}

export const updateRoleAccessConfig = async (id, data) => {
  const response = await axiosInstance.patch(`/role-access-config/${id}`, data)
  return response.data
}


export const getBillingConfig = async (plantId) => {
  const response = await axiosInstance.get(`/billing-config/plant/${plantId}`)
  return response.data
}

export const createBillingConfig = async (data) => {
  const response = await axiosInstance.post('/billing-config', data)
  return response.data
}

export const updateBillingConfig = async (id, data) => {
  const response = await axiosInstance.patch(`/billing-config/${id}`, data)
  return response.data
}